// Activity Reward Configuration API client (organizer web portal).
//
// Thin fetch wrappers over the /api/activity-configs + /api/activities REST
// endpoints, plus the form option enums (mirrors server/activityRewardDsl.js)
// and helpers to convert the 7-module form into the DSL `rawInput` shape.

import { getAuthBaseUrl, getToken } from './authApi';
import { getOrganizerToken } from './organizerApi';

function base() {
  return getAuthBaseUrl();
}

// The activity-config endpoints accept any authenticated user JWT, so this
// client is shared by the organizer portal AND the admin portal. Prefer the
// organizer token, fall back to the admin token.
function portalHeaders() {
  const token = getOrganizerToken() || getToken();
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function request(path, { method = 'GET', body } = {}) {
  const b = base();
  if (!b) return { success: false, error: 'API base not configured' };
  const res = await fetch(`${b}${path}`, {
    method,
    headers: portalHeaders(),
    body: body != null ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { success: false, error: data.error || `Request failed (${res.status})`, ...data };
  return data;
}

// --- Config CRUD ------------------------------------------------------------

export async function listConfigs(status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const data = await request(`/api/activity-configs${q}`);
  return Array.isArray(data.configs) ? data.configs : [];
}

export function createConfig(payload) {
  return request('/api/activity-configs', { method: 'POST', body: payload });
}

export function getConfig(id) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}`);
}

export function updateConfig(id, patch) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export function deleteConfig(id) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function compilePreview({ sourceType, rawInput, prompt }) {
  return request('/api/activity-configs/compile-preview', {
    method: 'POST',
    body: { sourceType, rawInput, prompt },
  });
}

export function compileConfig(id, { sourceType, rawInput, prompt, name } = {}) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/compile`, {
    method: 'POST',
    body: { sourceType, rawInput, prompt, name },
  });
}

/** Update a live activity atomically without taking it off the app. */
export function updatePublishedConfig(id, { sourceType, rawInput, prompt, name } = {}) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/update-published`, {
    method: 'POST',
    body: { sourceType, rawInput, prompt, name },
  });
}

export function publishConfig(id, eventId) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/publish`, {
    method: 'POST',
    body: { eventId },
  });
}

export function archiveConfig(id) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/archive`, { method: 'POST', body: {} });
}

export function simulateConfig(id, actions) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/simulate`, {
    method: 'POST',
    body: { actions },
  });
}

export function getStats(id) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/stats`);
}

/**
 * LLM co-pilot for point-rule / reward-economy design. Sends the LIVE editor
 * context (built from the form) plus an optional chat message; returns a short
 * reply, a rationale and an optimized `pointRules` array the caller can apply.
 */
export function optimizePointRules(id, { context, message, language } = {}) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/optimize-point-rules`, {
    method: 'POST',
    body: { context: context || {}, message: message || '', language: language || 'zh' },
  });
}

/**
 * Build the compact optimizer context from the editor form (sponsors, rewards,
 * target participants, lottery, current point rules) — shared by the chat UI.
 */
export function optimizerContextFromForm(form) {
  const a = form.activity || {};
  return {
    activityName: a.name || '',
    activityType: a.activityType || null,
    targetParticipants: a.targetParticipants === '' ? null : a.targetParticipants,
    startDate: a.startDate || null,
    endDate: a.endDate || null,
    routesCount: (a.routes || []).length,
    sponsors: (form.sponsors || []).map((s) => ({
      merchantName: s.merchantName || '',
      sponsorType: s.sponsorType || null,
      rewardBudgetDollars: s.rewardBudgetDollars === '' ? null : s.rewardBudgetDollars,
    })),
    rewards: (form.rewards || []).map((r) => ({
      title: r.title || '',
      kind: r.kind || null,
      mode: r.mode || null,
      costPoints: r.costPoints === '' ? null : r.costPoints,
      valueDollars: r.valueDollars === '' ? null : r.valueDollars,
      inventory: r.inventory === '' ? null : r.inventory,
      inventoryPolicy: r.inventoryPolicy || null,
    })),
    lottery: form.lottery && form.lottery.enabled ? {
      enabled: true,
      pointsPerTicket: form.lottery.pointsPerTicket === '' ? null : form.lottery.pointsPerTicket,
      maxEntriesPerUser: form.lottery.maxEntriesPerUser === '' ? null : form.lottery.maxEntriesPerUser,
      ticketTiers: (form.lottery.ticketTiers || [])
        .map((t) => ({ points: t.points === '' ? null : Number(t.points), tickets: t.tickets === '' ? null : Number(t.tickets) }))
        .filter((t) => t.points && t.tickets),
      ticketDrops: form.lottery.ticketDrops && form.lottery.ticketDrops.enabled ? {
        enabled: true,
        totalTickets: form.lottery.ticketDrops.totalTickets === '' ? null : Number(form.lottery.ticketDrops.totalTickets),
        maxPerUser: form.lottery.ticketDrops.maxPerUser === '' ? null : Number(form.lottery.ticketDrops.maxPerUser),
      } : { enabled: false },
      pools: (form.lottery.pools || []).map((p) => ({ name: p.name || '' })),
    } : { enabled: false },
    pointRules: (form.pointRules || []).map((r) => ({
      action: r.action,
      description: r.description || '',
      points: r.points === '' ? 0 : Number(r.points) || 0,
      dailyLimit: r.dailyLimit === '' ? null : r.dailyLimit,
      totalLimit: r.totalLimit === '' ? null : r.totalLimit,
      // Verification is derived from the action (see ACTION_VERIFICATION).
      verification: ACTION_VERIFICATION[r.action] || r.verification || 'gps',
      // Bind session_checkin rules to a specific timed mini-activity.
      metadata: r.action === 'session_checkin' && r.sessionRef ? { sessionRef: r.sessionRef } : {},
    })),
  };
}

export function drawLottery(id, poolKey) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/lottery/draw`, { method: 'POST', body: poolKey ? { poolKey } : {} });
}

/**
 * For a route-based activity: register a linked route-setup draft so the
 * activity surfaces in the creator's app draft box. The creator finishes route
 * node placement in the app; the web-configured rewards are prefilled into the
 * app's route completion-reward step, and publishing there activates both.
 */
export function createRouteDraft(id) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/route-draft`, { method: 'POST', body: {} });
}

/**
 * Web AI co-creation console: generate (or refine) a route's playable draft
 * (title, synopsis, per-node riddles) from its waypoints. Premium-gated on the
 * server. `body` = { waypoints, routeName, title?, synopsis?, city?, activityName?,
 * activityDescription?, difficulty?, language?, userFeedback?, existingNodes? }.
 */
export function aiRouteDraft(id, body) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/route-ai-draft`, { method: 'POST', body: body || {} });
}

/**
 * Finalize every route on "创建": fully-virtual routes become published playable
 * scripts on the web; routes with physical nodes get a per-route app draft. The
 * activity publishes once all routes are finalized.
 */
export function finalizeRoutes(id, body) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/finalize-routes`, { method: 'POST', body: body || {} });
}

/**
 * Ask a reward's sponsor to confirm issuance. `rewardRef` is the reward's index
 * in the form (or its key). The sponsor receives a system notification in the
 * app and confirms it in their merchant voucher-authentication screen.
 */
export function requestRewardIssuance(id, rewardRef) {
  return request(`/api/activity-configs/${encodeURIComponent(id)}/rewards/${encodeURIComponent(rewardRef)}/request-issuance`, {
    method: 'POST',
    body: {},
  });
}

// --- Merchant picker --------------------------------------------------------

/**
 * Search verified merchants for the sponsor picker.
 * @returns {Promise<Array<{ userId, name, username, avatarUrl, brandName, category, isOfficial, verificationLevel }>>}
 */
export async function searchMerchants(query, limit = 12) {
  const qs = `?query=${encodeURIComponent(query || '')}&limit=${encodeURIComponent(limit)}`;
  const data = await request(`/api/merchant/search${qs}`);
  return Array.isArray(data.merchants) ? data.merchants : [];
}

// --- Form option enums (mirror server/activityRewardDsl.js) -----------------

/** Activity type tags — same keys as app CreateGatheringView EVENT_TAG_KEYS. */
export const GATHERING_TAG_KEYS = ['outdoor', 'sports', 'social', 'culture', 'pets', 'wellness', 'learning', 'other'];

export const GATHERING_TAG_OPTIONS = GATHERING_TAG_KEYS.map((value) => ({ value }));

export const ACTION_OPTIONS = [
  { value: 'route_complete' }, // 完成路线 → 路线探索通关 (gps)
  { value: 'join_activity' },  // 参与活动 → 扫描二维码 (qr)
  { value: 'session_checkin' }, // 参与限时小活动 → 扫描二维码 (qr)
  { value: 'invite_friend' },  // 邀请好友 → 系统 (system)
  { value: 'review_submit' },  // 提交评价 → 系统 (system)
];

/**
 * Each action's verification method is fixed by how it can actually be proven,
 * so the editor auto-sets it (and shows it read-only) rather than letting the
 * organizer pick an incompatible method.
 *   route_complete   → gps    (完成路线探索通关)
 *   join_activity    → qr     (扫描二维码即得到积分)
 *   session_checkin  → qr     (扫描限时小活动二维码)
 *   invite_friend    → system
 *   review_submit    → system
 */
export const ACTION_VERIFICATION = {
  route_complete: 'gps',
  join_activity: 'qr',
  session_checkin: 'qr',
  invite_friend: 'system',
  review_submit: 'system',
};

export const VERIFICATION_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'gps', label: 'GPS' },
  { value: 'ai', label: 'AI' },
  { value: 'qr', label: 'QR' },
  { value: 'manual', label: 'Manual' },
];

export const BADGE_TYPE_OPTIONS = [
  { value: 'progression', label: 'Progression 进阶' },
  { value: 'specific', label: 'Specific 专属' },
];

export const BADGE_REQ_OPTIONS = [
  { value: 'routes_completed', label: 'Routes completed 完成N条路线' },
  { value: 'route_specific', label: 'Specific route 完成指定路线' },
  { value: 'hidden_location', label: 'Hidden location 找到隐藏点' },
  { value: 'sponsor_visit', label: 'Sponsor visit 到访赞助商' },
  { value: 'points_threshold', label: 'Points threshold 达到积分' },
];

export const REDEMPTION_MODE_OPTIONS = [
  { value: 'points_only', label: 'Points only 仅积分' },
  { value: 'points_and_badge', label: 'Points + Badge 积分+徽章' },
  { value: 'badge_only', label: 'Badge only 仅徽章' },
];

export const INVENTORY_POLICY_OPTIONS = [
  { value: 'limited', label: 'Limited 限量' },
  { value: 'first_come_first_served', label: 'First come first served 先到先得' },
  { value: 'unlimited', label: 'Unlimited 无限' },
];

export const REWARD_KIND_OPTIONS = [
  { value: 'coupon', label: 'Coupon 优惠券' },
  { value: 'physical', label: 'Physical 实物' },
  { value: 'experience', label: 'Experience 体验' },
  { value: 'digital', label: 'Digital 数字' },
];

export const EXPIRY_TYPE_OPTIONS = [
  { value: 'days', label: 'Days after issue 领取后N天' },
  { value: 'event_date', label: 'Event/redemption deadline 活动截止' },
  { value: 'fixed_date', label: 'Fixed date 指定日期' },
];

export const SPONSOR_TYPE_OPTIONS = [
  { value: 'main', label: 'Main Sponsor 主赞助' },
  { value: 'supporting', label: 'Supporting Sponsor 协办' },
];

// --- Blank rows for the dynamic form ---------------------------------------

export const BLANK_SPONSOR = { merchantName: '', merchantUserId: '', sponsorType: 'supporting', contribution: '', rewardBudgetDollars: '', rewardRefId: '' };
export const BLANK_POINT_RULE = { action: 'route_complete', description: '', points: 100, dailyLimit: '', totalLimit: '', verification: 'gps', sessionRef: '' };
export const BLANK_BADGE = { name: '', icon: '', iconUrl: '', type: 'progression', level: '', requirementType: 'routes_completed', requirementCount: 3, routeId: '', locationId: '', points: 0 };
export const BLANK_REWARD = { title: '', description: '', mode: 'points_only', costPoints: 500, requiredBadge: '', sponsorRef: '', kind: 'coupon', valueDollars: '', inventory: '', inventoryPolicy: 'limited', perUserLimit: '', totalBudgetDollars: '', expiryType: 'event_date', expiryValue: '' };

export const BLANK_LOTTERY = { enabled: false, entryRequirementPoints: '', pointsPerTicket: '', maxEntriesPerUser: '', lotteryDate: '', winnerSelection: 'random', ticketTiers: [], ticketDrops: { enabled: false, totalTickets: '', maxPerUser: '', startAt: '', endAt: '', dropRate: '' }, pools: [] };
export const BLANK_TICKET_TIER = { points: '', tickets: '' };

export const BLANK_LOTTERY_POOL = { name: '', prize: '', winnerCount: '', sponsorRef: '' };

export const BLANK_SESSION = { id: '', name: '', date: '', startTime: '', endTime: '', locationRef: '', routeRef: '', description: '' };

// --- Form <-> DSL rawInput conversion --------------------------------------

const dollarsToCents = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && v !== '' ? Math.round(n * 100) : null;
};
const intOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? n : null;
};
const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** Build the DSL `rawInput` object the compiler expects from the form state. */
export function formToRawInput(form) {
  const badgeKeyByName = new Map();
  const badges = (form.badges || []).map((b, i) => {
    const key = slug(b.name) || `badge_${i + 1}`;
    badgeKeyByName.set(b.name, key);
    const requirement = { type: b.requirementType };
    if (b.requirementType === 'routes_completed' || b.requirementType === 'points_threshold') {
      requirement.count = intOrNull(b.requirementCount);
    } else if (b.requirementType === 'route_specific') {
      requirement.routeId = b.routeId || null;
    } else if (b.requirementType === 'hidden_location' || b.requirementType === 'sponsor_visit') {
      requirement.locationId = b.locationId || null;
    }
    return {
      key,
      name: b.name,
      icon: b.icon || null,
      iconUrl: b.iconUrl || null,
      type: b.type,
      level: intOrNull(b.level),
      requirement,
      points: intOrNull(b.points) || 0,
    };
  });

  const geoLocations = (form.activity.locations || [])
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .map((p, i) => ({ id: p.id || `loc_${i + 1}`, name: p.name || '', lat: p.lat, lng: p.lng, isMainVenue: p.isMainVenue === true }));
  // Main venue (主会场) — the creator-designated map anchor for the app event.
  const mainVenue = geoLocations.find((p) => p.isMainVenue)
    || (form.activity.mainVenueLocationId ? geoLocations.find((p) => p.id === form.activity.mainVenueLocationId) : null)
    || null;
  const geoRoutes = (form.activity.routes || [])
    .map((r, i) => ({
      id: r.id || `route_${i + 1}`,
      name: r.name || '',
      // Co-created playable content (web AI console). Optional/additive.
      title: r.title || '',
      synopsis: r.synopsis || '',
      // Step-2 creation preferences (theme/style/inspiration/node types).
      theme: r.theme || '',
      routeStyle: r.routeStyle || '',
      inspiration: r.inspiration || '',
      preferredNodeTypes: Array.isArray(r.preferredNodeTypes) ? r.preferredNodeTypes : [],
      // 短期限定线截止时间（仅当 routeStyle === 'limited_time' 时生效）。
      limitedTimeDeadline: r.routeStyle === 'limited_time' ? (r.limitedTimeDeadline || '') : '',
      waypoints: (r.waypoints || [])
        .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
        .map((w) => ({
          name: w.name || '',
          lat: w.lat,
          lng: w.lng,
          isPhysical: w.isPhysical === true,
          ...(w.media && w.media.url
            ? { media: { url: String(w.media.url).trim(), type: w.media.type === 'video' ? 'video' : 'image' } }
            : {}),
          ...(w.riddle && (w.riddle.question || w.riddle.answer || w.riddle.hint)
            ? { riddle: { question: w.riddle.question || '', answer: w.riddle.answer || '', hint: w.riddle.hint || '', clueType: w.riddle.clueType || null } }
            : {}),
        })),
    }))
    .filter((r) => r.waypoints.length > 0);
  const anchor = mainVenue || geoLocations[0] || geoRoutes[0]?.waypoints?.[0] || null;

  const sessions = (form.activity.sessions || [])
    .map((s, i) => ({
      id: s.id || `session_${i + 1}`,
      name: (s.name || '').trim(),
      date: s.date || '',
      startTime: s.startTime || '',
      endTime: s.endTime || '',
      locationRef: s.locationRef || '',
      routeRef: s.routeRef || '',
      description: (s.description || '').trim(),
    }))
    .filter((s) => s.name || s.date);

  return {
    activity: {
      name: form.activity.name,
      description: form.activity.description,
      startDate: form.activity.startDate,
      endDate: form.activity.endDate,
      redemptionDeadline: form.activity.redemptionDeadline,
      location: mainVenue?.name || geoLocations[0]?.name || geoRoutes[0]?.name || form.activity.location || '',
      locationLat: anchor ? anchor.lat : (form.activity.locationLat ?? null),
      locationLng: anchor ? anchor.lng : (form.activity.locationLng ?? null),
      mainVenueLocationId: mainVenue?.id || null,
      locations: geoLocations,
      routes: geoRoutes,
      sessions,
      mediaUrls: (form.activity.mediaUrls || [])
        .filter((m) => m && m.url && String(m.url).trim())
        .map((m) => ({ url: String(m.url).trim(), type: m.type === 'video' ? 'video' : 'image' })),
      imageUrl: (() => {
        const list = (form.activity.mediaUrls || []).filter((m) => m?.url);
        const firstImage = list.find((m) => m.type !== 'video') || list[0];
        return firstImage ? String(firstImage.url).trim() : (form.activity.imageUrl || null);
      })(),
      activityType: form.activity.activityType,
      targetParticipants: intOrNull(form.activity.targetParticipants),
    },
    sponsors: (form.sponsors || []).map((s) => ({
      merchantName: s.merchantName,
      merchantUserId: s.merchantUserId,
      sponsorType: s.sponsorType,
      contribution: s.contribution,
      rewardBudgetCents: dollarsToCents(s.rewardBudgetDollars),
      rewardRefId: s.rewardRefId || null,
    })),
    pointRules: (form.pointRules || []).map((r) => ({
      action: r.action,
      description: r.description,
      points: intOrNull(r.points) || 0,
      dailyLimit: intOrNull(r.dailyLimit),
      totalLimit: intOrNull(r.totalLimit),
      // Verification is derived from the action (see ACTION_VERIFICATION).
      verification: ACTION_VERIFICATION[r.action] || r.verification,
      // Bind session_checkin rules to a specific timed mini-activity so its QR
      // awards the right points and the selection round-trips on reload.
      metadata: r.action === 'session_checkin' && r.sessionRef ? { sessionRef: r.sessionRef } : {},
    })),
    badges,
    lottery: {
      enabled: !!form.lottery.enabled,
      entryRequirementPoints: intOrNull(form.lottery.entryRequirementPoints),
      pointsPerTicket: intOrNull(form.lottery.pointsPerTicket),
      maxEntriesPerUser: intOrNull(form.lottery.maxEntriesPerUser),
      lotteryDate: form.lottery.lotteryDate || null,
      winnerSelection: form.lottery.winnerSelection,
      // Customizable N points → M tickets exchange tiers. Blank rows dropped.
      ticketTiers: (form.lottery.ticketTiers || [])
        .map((t) => ({ points: intOrNull(t.points), tickets: intOrNull(t.tickets) }))
        .filter((t) => t.points && t.points > 0 && t.tickets && t.tickets > 0),
      // Random ticket drops seeded into route explorations.
      ticketDrops: form.lottery.ticketDrops && form.lottery.ticketDrops.enabled ? {
        enabled: true,
        totalTickets: intOrNull(form.lottery.ticketDrops.totalTickets),
        maxPerUser: intOrNull(form.lottery.ticketDrops.maxPerUser),
        startAt: form.lottery.ticketDrops.startAt || null,
        endAt: form.lottery.ticketDrops.endAt || null,
        dropRate: form.lottery.ticketDrops.dropRate === '' || form.lottery.ticketDrops.dropRate == null
          ? null : Number(form.lottery.ticketDrops.dropRate),
      } : { enabled: false },
      pools: (form.lottery.pools || []).map((p, i) => ({
        key: p.key || undefined,
        name: p.name || '',
        prize: p.prize || null,
        winnerCount: intOrNull(p.winnerCount),
        sponsorRef: p.sponsorRef || null,
        sortOrder: i,
      })),
    },
    rewards: (form.rewards || []).map((rw) => ({
      title: rw.title,
      description: rw.description,
      mode: rw.mode,
      costPoints: rw.mode === 'badge_only' ? null : intOrNull(rw.costPoints),
      requiredBadge: rw.mode === 'points_only' ? null : (badgeKeyByName.get(rw.requiredBadge) || slug(rw.requiredBadge) || null),
      sponsorRef: rw.sponsorRef || null,
      kind: rw.kind,
      valueCents: dollarsToCents(rw.valueDollars),
      inventory: rw.inventoryPolicy === 'unlimited' ? null : intOrNull(rw.inventory),
      inventoryPolicy: rw.inventoryPolicy,
      perUserLimit: intOrNull(rw.perUserLimit),
      totalBudgetCents: dollarsToCents(rw.totalBudgetDollars),
      expiry: { type: rw.expiryType, value: rw.expiryType === 'days' ? intOrNull(rw.expiryValue) : rw.expiryValue || null },
    })),
  };
}

const STATUS_ZH = { draft: '草稿', compiled: '待发布', published: '已上架', archived: '已下架' };
const STATUS_EN = { draft: 'Draft', compiled: 'Ready to publish', published: 'Published', archived: 'Unpublished' };

export function statusLabel(status, lang = 'zh') {
  const map = lang === 'en' ? STATUS_EN : STATUS_ZH;
  return map[status] || status || '—';
}

export function formatIsoDate(iso, lang = 'zh') {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'en' ? 'en-AU' : 'zh-CN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
}
